// services/permissionService.js
import axios from 'axios';

class PermissionService {
  constructor() {
    this.userPermissions = new Set();
    this.userRole = null;
    this.initialized = false;
  }

  async initializePermissions(userId, businessId) {
    try {
      const response = await axios.get(`/api/users/${userId}/permissions`, {
        params: {
          businessId: businessId
        }
      });
      const data = response.data;
      
      if (data.code === 'ok') {
        this.userPermissions = new Set(data.data.permissions || []);
        this.userRole = data.data.role;
        this.initialized = true;
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to initialize permissions:', error);
      return false;
    }
  }

  hasPermission(permission) {
    if (!this.initialized) {
      console.warn('Permissions not initialized');
      return false;
    }
    return this.userPermissions.has(permission);
  }

  hasRole(role) {
    return this.userRole === role;
  }

  canAccessRoute(routeName) {
    // If permissions aren't initialized, default to allowing access
    // This prevents blocking navigation during app startup
    if (!this.initialized) {
      console.warn('Permissions not initialized, allowing route access');
      return true;
    }

    // Owners have access to everything
    if (this.hasRole('owner')) {
      return true;
    }

    // Define route permissions mapping
    const routePermissions = {
      'Dashboard': 'view_dashboard',
      'Calendar': 'view_events',
      'Appointments': 'view_events',
      'Services': 'view_services',
      'Providers': 'view_providers',
      'Clients': 'view_clients',
      'ClientDetail': 'view_clients',
      'Booking Rules': 'view_booking_rules',
      'Business Information': 'view_business_info',
      'Team': 'view_team',
      'Tags': 'view_tags',
      'Booking Forms': 'view_booking_form',
      'Account Settings': null // Account settings available to all authenticated users
    };

    // Check specific permission for the route
    const requiredPermission = routePermissions[routeName];
    if (requiredPermission) {
      return this.hasPermission(requiredPermission);
    }

    // Default to allowing access if no specific permission defined
    return true;
  }

  canAccessSection(sectionId) {
    // Owners have access to everything
    if (this.hasRole('owner')) {
      return true;
    }

    // Define section permissions
    const sectionPermissions = {
      'dashboard': 'view_dashboard',
      'calendar': 'view_events',
      'appointments': 'view_events',
      'services': 'view_services',
      'providers': 'view_providers',
      'clients': 'view_clients',
      'settings': 'view_settings', // This will control the entire settings section
      'account-settings': null // Account settings available to all authenticated users
    };

    const requiredPermission = sectionPermissions[sectionId];
    if (requiredPermission) {
      return this.hasPermission(requiredPermission);
    }

    // Default to allowing access if no specific permission defined
    return true;
  }

  canAccessSubSection(parentSection, subSectionId) {
    // If user can't access parent section, they can't access subsections
    if (!this.canAccessSection(parentSection)) {
      return false;
    }

    // Owners have access to everything
    if (this.hasRole('owner')) {
      return true;
    }

    // Define subsection permissions
    const subSectionPermissions = {
      'info': 'view_business_info',
      'working-hours': 'view_business_info',
      'booking-form': 'view_booking_form',
      'rules': 'view_booking_rules',
      'features': 'view_features',
      'team': 'view_team',
      'tags': 'view_tags'
    };

    const requiredPermission = subSectionPermissions[subSectionId];
    if (requiredPermission) {
      return this.hasPermission(requiredPermission);
    }

    return true;
  }

  clear() {
    this.userPermissions.clear();
    this.userRole = null;
    this.initialized = false;
  }
}

export default new PermissionService();